import { Navigate, Route, Routes } from 'react-router-dom';

import { Layout } from './components/Layout.tsx';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { useUser } from './hooks/useUser.ts';
import { AdminAlertsPage } from './routes/AdminAlertsPage.tsx';
import { AdminPermissionsPage } from './routes/AdminPermissionsPage.tsx';
import { AdminAuditLogPage } from './routes/AdminAuditLogPage.tsx';
import { AdminPage } from './routes/AdminPage.tsx';
import { AdminSyncStatePage } from './routes/AdminSyncStatePage.tsx';
import { AdminVendorLogPage } from './routes/AdminVendorLogPage.tsx';
import { AdminWriteTasksPage } from './routes/AdminWriteTasksPage.tsx';
import { ArAgingPage } from './routes/ArAgingPage.tsx';
import { RfidExclusionsPage } from './routes/RfidExclusionsPage.tsx';
import { InventoryPage } from './routes/InventoryPage.tsx';
import { SalesPerformancePage } from './routes/SalesPerformancePage.tsx';
import { CashSaleDetailPage } from './routes/CashSaleDetailPage.tsx';
import { ChangePasswordPage } from './routes/ChangePasswordPage.tsx';
import { CashSalesListPage } from './routes/CashSalesListPage.tsx';
import { CustomerDetailPage } from './routes/CustomerDetailPage.tsx';
import { CustomersListPage } from './routes/CustomersListPage.tsx';
import { DeliveryOrderDetailPage } from './routes/DeliveryOrderDetailPage.tsx';
import { DeliveryOrdersListPage } from './routes/DeliveryOrdersListPage.tsx';
import { InvoiceDetailPage } from './routes/InvoiceDetailPage.tsx';
import { InvoicesListPage } from './routes/InvoicesListPage.tsx';
import { LoginPage } from './routes/LoginPage.tsx';
import { OperationsPage } from './routes/OperationsPage.tsx';
import { OverviewPage } from './routes/OverviewPage.tsx';
import { PurchaseDetailPage } from './routes/PurchaseDetailPage.tsx';
import { PurchaseOrderDetailPage } from './routes/PurchaseOrderDetailPage.tsx';
import { PurchaseOrdersListPage } from './routes/PurchaseOrdersListPage.tsx';
import { DispatchKeyingPage } from './routes/DispatchKeyingPage.tsx';
import { DispatchListPage } from './routes/DispatchListPage.tsx';
import { DispatchShortagesPage } from './routes/DispatchShortagesPage.tsx';
import { ReceiptKeyingPage } from './routes/ReceiptKeyingPage.tsx';
import { ReceivingListPage } from './routes/ReceivingListPage.tsx';
import { PurchasesListPage } from './routes/PurchasesListPage.tsx';
import { SalesOrderDetailPage } from './routes/SalesOrderDetailPage.tsx';
import { SalesOrdersListPage } from './routes/SalesOrdersListPage.tsx';
import { SalesReturnDetailPage } from './routes/SalesReturnDetailPage.tsx';
import { SalesReturnsListPage } from './routes/SalesReturnsListPage.tsx';
import { SkuDetailPage } from './routes/SkuDetailPage.tsx';
import { SkusListPage } from './routes/SkusListPage.tsx';
import { StockCountsListPage } from './routes/StockCountsListPage.tsx';
import { StockCountVariancePage } from './routes/StockCountVariancePage.tsx';
import { SupplierDetailPage } from './routes/SupplierDetailPage.tsx';
import { SuppliersListPage } from './routes/SuppliersListPage.tsx';

// Role groups — must match server-side capability sets in
// apps/server/src/middleware/capabilities.ts.
const FINANCIAL = ['admin', 'manager', 'viewer', 'superadmin', 'director', 'finance_manager'] as const;
// Ops + financial roles can both view SKUs, DOs, SRs, POs, GRNs.
// Money fields are scrubbed server-side for ops-only roles.
const OPS_OR_FINANCIAL = [
  'admin',
  'manager',
  'viewer',
  'operator',
  'superadmin',
  'director',
  'finance_manager',
  'operation',
] as const;
// Operations floor: RFID activity + Stock counts. Everyone in OPS_OR_FINANCIAL
// plus `logistics`, which is scoped to ONLY these two pages.
const FLOOR = [...OPS_OR_FINANCIAL, 'logistics'] as const;
// RFID exclusions: admins plus the ops floor (operation/operator/logistics).
// Must match requireAnyRole in routes/admin.ts (rfidExclusionsRouter).
const RFID_EXCLUSIONS = ['admin', 'superadmin', 'operation', 'operator', 'logistics'] as const;
const SUPERADMIN = ['admin', 'superadmin'] as const;

/**
 * The dashboard's "/" landing page is the financial Overview, but the
 * operation/operator/logistics roles have no access to that. Redirect them to
 * the first page they can see (SKUs for ops; Operations for logistics).
 */
function HomeForRole() {
  const { user } = useUser();
  if (user?.role === 'logistics') {
    return <Navigate to="/operations" replace />;
  }
  if (user?.role === 'operation' || user?.role === 'operator') {
    return <Navigate to="/skus" replace />;
  }
  return (
    <ProtectedRoute permission="overview.view">
      <OverviewPage />
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeForRole />} />

        {/* Self-service password change — any authenticated user. */}
        <Route
          path="account"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="customers"
          element={
            <ProtectedRoute permission="customers.view">
              <CustomersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customers/:id"
          element={
            <ProtectedRoute permission="customers.view">
              <CustomerDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="suppliers"
          element={
            <ProtectedRoute permission="suppliers.view">
              <SuppliersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="suppliers/:id"
          element={
            <ProtectedRoute permission="suppliers.view">
              <SupplierDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="skus"
          element={
            <ProtectedRoute permission="skus.view">
              <SkusListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="skus/:id"
          element={
            <ProtectedRoute permission="skus.view">
              <SkuDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="invoices"
          element={
            <ProtectedRoute permission="invoices.view">
              <InvoicesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="invoices/:id"
          element={
            <ProtectedRoute permission="invoices.view">
              <InvoiceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ar-aging"
          element={
            <ProtectedRoute permission="ar_aging.view">
              <ArAgingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-performance"
          element={
            <ProtectedRoute permission="sales_performance.view">
              <SalesPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="inventory"
          element={
            <ProtectedRoute permission="inventory.view">
              <InventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-counts"
          element={
            <ProtectedRoute permission="stock_counts.view">
              <StockCountsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-counts/:billId"
          element={
            <ProtectedRoute permission="stock_counts.view">
              <StockCountVariancePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="sales-returns"
          element={
            <ProtectedRoute permission="sales_returns.view">
              <SalesReturnsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-returns/:id"
          element={
            <ProtectedRoute permission="sales_returns.view">
              <SalesReturnDetailPage />
            </ProtectedRoute>
          }
        />

        <Route path="sales-orders" element={<ProtectedRoute permission="sales_orders.view"><SalesOrdersListPage /></ProtectedRoute>} />
        <Route path="sales-orders/:id" element={<ProtectedRoute permission="sales_orders.view"><SalesOrderDetailPage /></ProtectedRoute>} />

        <Route path="cash-sales" element={<ProtectedRoute permission="cash_sales.view"><CashSalesListPage /></ProtectedRoute>} />
        <Route path="cash-sales/:id" element={<ProtectedRoute permission="cash_sales.view"><CashSaleDetailPage /></ProtectedRoute>} />

        <Route path="delivery-orders" element={<ProtectedRoute permission="delivery_orders.view"><DeliveryOrdersListPage /></ProtectedRoute>} />
        <Route path="delivery-orders/:id" element={<ProtectedRoute permission="delivery_orders.view"><DeliveryOrderDetailPage /></ProtectedRoute>} />

        <Route path="purchase-orders" element={<ProtectedRoute permission="purchase_orders.view"><PurchaseOrdersListPage /></ProtectedRoute>} />
        <Route path="receiving" element={<ProtectedRoute permission="purchase_orders.receive"><ReceivingListPage /></ProtectedRoute>} />
        <Route path="dispatch" element={<ProtectedRoute permission="delivery_orders.dispatch"><DispatchListPage /></ProtectedRoute>} />
        <Route path="dispatch/:doNo" element={<ProtectedRoute permission="delivery_orders.dispatch"><DispatchKeyingPage /></ProtectedRoute>} />
        <Route path="dispatch-exceptions" element={<ProtectedRoute permission="operations.view"><DispatchShortagesPage /></ProtectedRoute>} />
        <Route path="receiving/:receiptNo" element={<ProtectedRoute permission="purchase_orders.receive"><ReceiptKeyingPage /></ProtectedRoute>} />
        <Route path="purchase-orders/:id" element={<ProtectedRoute permission="purchase_orders.view"><PurchaseOrderDetailPage /></ProtectedRoute>} />

        <Route path="purchases" element={<ProtectedRoute permission="purchases.view"><PurchasesListPage /></ProtectedRoute>} />
        <Route path="purchases/:id" element={<ProtectedRoute permission="purchases.view"><PurchaseDetailPage /></ProtectedRoute>} />

        <Route path="operations" element={<ProtectedRoute permission="operations.view"><OperationsPage /></ProtectedRoute>} />

        <Route
          path="admin"
          element={
            <ProtectedRoute permission="admin.users">
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/permissions"
          element={
            <ProtectedRoute permission="admin.permissions">
              <AdminPermissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/sync-state"
          element={
            <ProtectedRoute permission="admin.sync_state">
              <AdminSyncStatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/audit-log"
          element={
            <ProtectedRoute permission="admin.audit_log">
              <AdminAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/alerts"
          element={
            <ProtectedRoute permission="admin.alerts">
              <AdminAlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/vendor-log"
          element={
            <ProtectedRoute permission="admin.vendor_log">
              <AdminVendorLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/write-tasks"
          element={
            <ProtectedRoute permission="write_tasks.view">
              <AdminWriteTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/rfid-exclusions"
          element={
            <ProtectedRoute permission="admin.rfid_exclusions">
              <RfidExclusionsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
